import type { BreadJsonLd, TvJsonLd } from "~/@types/meta";
import type { G_GetVideoQuery } from "~/lib/graphql";
import { formatDateToHyphenated } from "~/util/format-date";

type GetJsonLdDataArgs = { videoData: G_GetVideoQuery["video"] };

let categoryId = "";
let categoryName = "";

export const getJsonLdData = ({ videoData }: GetJsonLdDataArgs) => {
  const { __typename, id, titleName, seasonName, metaDescription, categories, genres, casts, startPublicAt } =
    videoData;

  if (categories !== undefined && categories.length > 0) {
    const animeCategoryId = categories.find((item) => item.name === "アニメ")?.id;
    const animeCategoryName = categories.find((item) => item.name === "アニメ")?.name;

    if (animeCategoryId !== undefined) {
      categoryId = animeCategoryId;
    } else {
      categoryId = categories[0].id;
    }

    if (animeCategoryName !== undefined) {
      categoryName = animeCategoryName;
    } else {
      categoryName = categories[0].name;
    }
  }

  const detailBreadcrumbJsonLdData: BreadJsonLd = {
    "@context": "http://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        item: "https://tv.dmm.com/vod/",
        name: "ホーム",
      },
      {
        "@type": "ListItem",
        position: 2,
        item: `https://tv.dmm.com/vod/list/?categories=${categoryId}`,
        name: categoryName,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: titleName === seasonName ? titleName : `${titleName} ${seasonName}`,
      },
    ],
  };

  const createImage = () => {
    if (__typename === "VideoLegacySeason") {
      const { packageLargeImage, packageImage } = videoData;
      return packageLargeImage || packageImage;
    }

    if (__typename === "VideoSeason" || __typename === "VideoStageSeason" || __typename === "VideoSpotLiveSeason") {
      const { keyVisualImage, keyVisualWithoutLogoImage, packageImage } = videoData;
      return keyVisualImage || keyVisualWithoutLogoImage || packageImage;
    }

    return "";
  };

  const createVideoObject = () => {
    if (__typename === "VideoSeason") {
      const {
        pvEpisode: { edges },
      } = videoData;

      if (edges.length === 0) return {};

      const {
        sampleMovie,
        playInfo: { duration },
      } = edges[0].node;

      if (sampleMovie === "") return {};

      return {
        "@type": "VideoObject",
        name: titleName === seasonName ? titleName : `${titleName} ${seasonName}`,
        description: metaDescription,
        duration: `PT${duration}S`,
        contentUrl: sampleMovie,
        thumbnailUrl: createImage(),
        uploadDate: formatDateToHyphenated(startPublicAt),
      } as const;
    }

    if (__typename === "VideoLegacySeason") {
      const {
        sampleMovie,
        content: {
          playInfo: { duration },
        },
      } = videoData;

      if (sampleMovie === undefined || sampleMovie === null) return {};

      return {
        "@type": "VideoObject",
        name: titleName === seasonName ? titleName : `${titleName} ${seasonName}`,
        description: metaDescription,
        duration: `PT${duration}S`,
        thumbnailUrl: sampleMovie.thumbnail ?? "",
        contentUrl: sampleMovie.url,
        uploadDate: formatDateToHyphenated(startPublicAt),
      } as const;
    }

    return {};
  };

  const createAgreeGateRating = () => {
    if (__typename === "VideoSpotLiveSeason") return {};

    const {
      reviewSummary: { averagePoint, reviewerCount },
    } = videoData;

    if (reviewerCount === 0) return {};

    return {
      AggregateRating: {
        "@type": "AggregateRating",
        ratingValue: Number(averagePoint.toFixed(2)),
        ratingCount: reviewerCount,
      },
    } as const;
  };

  const isMovieCategory = categories?.some((item) => item.name === "映画");

  const detailTvJsonLdData: TvJsonLd = {
    "@context": "http://schema.org",
    "@type": isMovieCategory ? "Movie" : "TVSeries",
    url: `https://tv.dmm.com/vod/detail/?season=${id}`,
    name: titleName === seasonName ? titleName : `${titleName} ${seasonName}`,
    description: metaDescription,
    image: createImage(),
    genre: genres.map(({ name }) => name),

    actor: casts.map(({ actorName }) => {
      return { "@type": "Person", name: actorName };
    }),

    ...(Object.keys(createVideoObject()).length !== 0
      ? {
          trailer: {
            ...createVideoObject(),
            ...createAgreeGateRating(),
          },
        }
      : {}),
  };

  return { detailBreadcrumbJsonLdData, detailTvJsonLdData };
};
